import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	Dialog,
	Form,
	FormItem,
	Select,
	SelectOption,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	clearDefaultApp,
	DefaultAppDialogViewModel,
	getDefaultAppDialogViewModel,
	IntegrationAppOptionModel,
	setDefaultApp
} from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	EMPTY_ARRAY,
	isNil
} from "client/Misc/utils";

export type ManageDefaultAppDialogProps = {
	onClose: () => void;
	onSave: (defaultAppId: number | null) => void;
};

export const ManageDefaultAppDialog = (props: ManageDefaultAppDialogProps): ReactElement => {
	const {
		onClose,
		onSave
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [apps, setApps] = useState<readonly IntegrationAppOptionModel[]>(EMPTY_ARRAY);
	const [model, setModel] = useState<DefaultAppFormModel>(EMPTY_FORM_VALUE);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<DefaultAppFormModel> = useFormHandler<DefaultAppFormModel>(
		model,
		setModel,
		"NoValidation"
	);

	const options = useMemo(
		(): readonly SelectOption<number | null>[] => [
			{
				text: localize("_NotSelected"),
				value: null
			},
			...apps.map(x => ({
				text: x.name,
				value: x.id
			}))
		],
		[apps]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				if (isNil(model.appId)) {
					await clearDefaultApp(globalCancellation);
				} else {
					await setDefaultApp(
						{ appId: model.appId },
						globalCancellation
					);
				}

				onSave(model.appId);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);

				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			isBusy,
			model,
			onSave
		]
	);

	const stableOnClose: () => void = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const viewModel: DefaultAppDialogViewModel = await getDefaultAppDialogViewModel(signal);

					setApps(viewModel.apps);
					setModel({ appId: viewModel.defaultAppId });
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, stableOnClose]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="manage-default-integration-app-dialog"
		>
			<Dialog.Header
				title={localize("_ManageDefaultApp")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={saveHandler}>
					<FormItem label={localize("_DefaultApp")}>
						<Select
							{...formHandler.for("appId")
								.attachToSelect()
							}
							options={options}
							menuTitle={localize("_DefaultApp")}
						/>
						<ValidationError {...formHandler.for("appId").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						isDisabled={isBusy}
						onClick={saveHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ManageDefaultAppDialog.displayName = "ManageDefaultAppDialog";

export type ManageDefaultAppDialogInitialData = Omit<
	ManageDefaultAppDialogProps,
	"onClose" | "onSave"
>;

export function useManageDefaultAppDialog(): [
	element: ReactElement | null,
	open: (props: ManageDefaultAppDialogInitialData) => Promise<TryResult<number | null>>
] {
	return useDataInputDialog<ManageDefaultAppDialogInitialData, number | null>(
		(props, onClose, onSave) => (
			<ManageDefaultAppDialog
				{...props}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

type DefaultAppFormModel = {
	appId: number | null;
};

const EMPTY_FORM_VALUE: DefaultAppFormModel = {
	appId: null
};
